import React, { Key, useEffect } from 'react';
import { useAsyncList } from '@react-stately/data';
import { MenuTriggerAction } from '@react-types/combobox';
import { LoadingState } from '@react-types/shared';
import cn from 'classnames';

import { signal } from '@nw/shared/config';

import { useQueryClientDistrictsHook } from '../../hooks/useQueryClientDistrictsHook';
import { useStateAndDistricts } from '../../hooks/useStateAndDistricts';
import {
  getDistrictIdFromDistrictName,
  getDistrictNameFromDistrictId,
  getFilteredDistricts
} from '../../utils/ComboBoxUtils';

import { ComboBox } from '../ComboboxWithInitialValue';
import {
  AsyncListLoadFunction,
  District,
  OptionsType,
  OverlayOptions
} from '../types';

export interface DistrictComboBoxProps {
  stateId: string;
  label: string;
  options?: Array<OptionsType>;
  onSelectionChange?: (e: Key) => void;
  selectedKey?: string;
  placeholder?: string;
  renderListItem?: any;
  onBlur?: (e: React.FocusEvent) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onOpenChange?: (
    isOpen: boolean,
    menuTrigger?: MenuTriggerAction | undefined
  ) => void;
  error?: string;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  defaultFilter?: any;
  isDisabled?: boolean;
  apiStatus?: string;
  autoFocus?: boolean;
  listItemStyles?: string;
  itemContentStyles?: string;
  loadingState?: LoadingState;
  onLoadMore?: () => any;
  overlayOptions?: OverlayOptions;
  getListItemTextValue?: (T: any) => any;
  initialDistrictValue?: string;
  onStateChange: () => void;
  getDisplayValueFromSelectedState?: (T: any) => string;
  helperText?: string;
  hintClassName?: string;
  enableDataMask?: boolean;
  mobilePlaceholder?: string;
}

export const DistrictsComboBox = (props: DistrictComboBoxProps) => {
  const {
    stateId,
    onOpenChange,
    onInputChange,
    onSelectionChange,
    selectedKey,
    inputValue,
    inputFieldStyles = '',
    buttonStyles = '',
    placeholder = '',
    error,
    initialDistrictValue,
    onStateChange,
    getDisplayValueFromSelectedState,
    helperText,
    hintClassName,
    enableDataMask,
    mobilePlaceholder,
    ...others
  } = props;

  const { districts } = useStateAndDistricts();

  const onSelectionChangeFunc = (key) => {
    onSelectionChange && onSelectionChange(key);
  };

  const onOpenChangeFunc = (key: any) => {
    onOpenChange && onOpenChange(key);
  };

  const onInputChangeFunc = (value) => {
    onInputChange && onInputChange(value);
  };

  const districtsDataPromise = useQueryClientDistrictsHook(signal, stateId);

  const loadItems: AsyncListLoadFunction<
    { district_id: string; district_name: any },
    any
  > = async ({ signal, cursor, filterText, selectedKeys, items }) => {
    const json = await districtsDataPromise;

    const selectedKey = (selectedKeys as Set<React.Key>).values().next().value;

    districts.current = json.districts; //FIXME: use updateDistricts from useStateAndDistricts

    const filteredDistricts = Boolean(initialDistrictValue && !selectedKey)
      ? json.districts
      : getFilteredDistricts(filterText, json.districts);

    return {
      items: filteredDistricts,
      cursor: null,
      filterText: filterText
    };
  };

  useEffect(() => {
    list.removeSelectedItems();
    list.setFilterText(''); //NOTE: load function in useAsyncList is called when setFilterText is called
    if (stateId) {
      onStateChange();
    }
  }, [stateId]);

  const list = useAsyncList({
    load: loadItems,
    initialFilterText: initialDistrictValue ?? '',
    initialSelectedKeys: [],
    getKey: (item: District) => item.district_id
  });

  const getInputTextFromValue = (value, list) => {
    return getDistrictNameFromDistrictId(value, list);
  };

  const getValueFromInputText = (text, list) => {
    return getDistrictIdFromDistrictName(text, list);
  };

  return (
    <ComboBox
      {...others}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChangeFunc}
      onInputChange={onInputChangeFunc}
      onOpenChange={onOpenChangeFunc}
      inputFieldStyles={cn('w-full', inputFieldStyles)}
      buttonStyles={buttonStyles}
      getListItemKey={(item) => item.district_id}
      placeholder={placeholder}
      getListItemTextValue={(item) => item.district_name}
      renderListItem={({ item }) => (
        <p key={item.district_id}>{item.district_name}</p>
      )}
      error={error}
      getInputTextFromValue={getInputTextFromValue}
      getValueFromInputText={getValueFromInputText}
      initialValue={initialDistrictValue}
      list={list}
      getDisplayValueFromSelectedState={getDisplayValueFromSelectedState}
      helperText={helperText}
      hintClassName={hintClassName}
      enableDataMask={enableDataMask}
      mobilePlaceholder={mobilePlaceholder}
    />
  );
};
